import { useCallback, useMemo } from "react"
import { Box } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { fetchCustomersForMerchant, FetchCustomersResponse } from "../../../../modules/customers/customerApi"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"
import { AccountingDebtor } from "../../../../modules/merchant/accounting/accounting/accountingTypes"
import { ApiQueryAutocomplete } from "../../../../core/api/components/ApiQueryAutocomplete"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { useMerchant } from "../../../../modules/merchants/merchantHooks"
import { useApiQuery } from "../../../../core/api/useApiQuery"

type GroupedDebtor = AccountingDebtor &
    Readonly<{
        group: string
    }>
type StyleProps = Readonly<{
    isFullWidth: boolean
}>
const useStyles = makeStyles<StyleProps>()((theme, { isFullWidth }) => ({
    input: {
        width: isFullWidth ? "100%" : "16rem",
        backgroundColor: theme.palette.common.white,
    },
}))

type StandardCustomerSelectProps = Readonly<{
    merchantIdValue?: number | string
    customerId?: string
    customerGroupId?: string
    setCustomerId: (customerId: string | undefined) => void
    setCustomerGroupId: (customerGroupId: string | undefined) => void
    isFullWidth?: boolean
    isFilterDrawer?: boolean
    className?: string
}>

export function StandardCustomerSelect({
    merchantIdValue,
    customerId,
    customerGroupId,
    setCustomerId,
    setCustomerGroupId,
    isFullWidth = false,
    isFilterDrawer = false,
    className,
}: StandardCustomerSelectProps) {
    const { classes, cx } = useStyles({ isFullWidth })
    const { l10n } = useAppLocalization()
    const { merchantId } = useMerchant()

    const allItem = useMemo<GroupedDebtor>(
        () => ({
            type: "customer",
            group: l10n.getString("select-customer-all"),
            id: -1,
            name: l10n.getString("select-customer-all"),
            isActive: true,
            hasSales: false,
        }),
        [l10n],
    )

    const selector = useCallback(
        (data: FetchCustomersResponse) => {
            const mappedData = data.customers.flatMap((customer) => {
                if (customer.isOfficeHotel && customer.userGroups) {
                    const groupDebitors: GroupedDebtor[] = customer.userGroups.map((userGroup) => ({
                        type: "customerGroup",
                        group: customer.name,
                        id: userGroup.id,
                        customerId: customer.id,
                        name: userGroup.name,
                        isActive: userGroup.isActive,
                        hasSales: false,
                    }))
                    return groupDebitors
                } else {
                    const customerDebitor: GroupedDebtor = {
                        type: "customer",
                        group: customer.name,
                        id: customer.id,
                        name: customer.name,
                        isActive: customer.isActive,
                        hasSales: false,
                    }
                    return customerDebitor
                }
            })

            const sortedData = mappedData
                .filter((el) => el !== undefined)
                .sort((a, b) => {
                    const sortedByGroup = -b.group.localeCompare(a.group)
                    if (sortedByGroup === 0) {
                        return -b.name.localeCompare(a.name)
                    } else {
                        return sortedByGroup
                    }
                })

            sortedData.unshift(allItem)

            return sortedData
        },
        [allItem],
    )

    const {
        data: result,
        isError,
        isLoading,
    } = useApiQuery(fetchCustomersForMerchant, {
        queryName: "customers-for-merchant",
        dependencies: {
            merchantId: merchantIdValue ? +merchantIdValue : merchantId,
            isActive: true,
        },
        select: selector,
    })

    const data = useMemo(() => result ?? [allItem], [result, allItem])

    const selectedValue = useMemo(() => {
        if (!customerId) return allItem
        return (
            data.find((debtor) => {
                if (debtor.type === "customerGroup" && customerGroupId) {
                    return debtor.customerId === +customerId && debtor.id === +customerGroupId
                }

                return debtor.type === "customer" && debtor.id === +customerId
            }) || allItem
        )
    }, [customerId, customerGroupId, data])

    return (
        <Box className={cx(className)}>
            <LocalizedStrict id={isFilterDrawer ? "select-debtor" : "select-customer"} attrs={{ label: true }}>
                <ApiQueryAutocomplete
                    isLoading={isLoading}
                    isError={isError}
                    disableClearable
                    options={data}
                    value={selectedValue}
                    defaultValue={data[0]}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    groupBy={(option) => option.group}
                    getOptionLabel={(option) => `${option.name}`}
                    renderOption={(props, option) => (
                        <Box component="li" sx={{ ml: "0.5rem" }} {...props}>
                            {option.name}
                        </Box>
                    )}
                    onChange={(e, value) => {
                        if (value.type === "customerGroup") {
                            setCustomerId(value.customerId.toString())
                            setCustomerGroupId(value.id.toString())
                        } else if (value.type === "customer" && value.id > 0) {
                            setCustomerId(value.id.toString())
                            setCustomerGroupId(undefined)
                        } else {
                            setCustomerId(undefined)
                            setCustomerGroupId(undefined)
                        }
                    }}
                    className={classes.input}
                />
            </LocalizedStrict>
        </Box>
    )
}
